import { useIsMutating } from '@tanstack/react-query'
import { isValidElement, type JSX, useEffect, useEffectEvent } from 'react'
import { type Toast, useToastService } from '../../services/toast-service.tsx'
import { REFUSED_CHANGE_MESSAGE } from './admin-required-override.ts'

/** The refusal toast wraps the refusal text in a fragment and keeps it until dismissed. */
const isRefusalToast = (toast: Toast) =>
  toast.transience === 'sticky' &&
  isValidElement<{ children?: unknown }>(toast.message) &&
  toast.message.props.children === REFUSED_CHANGE_MESSAGE

/**
 * Closes an open dialog once the permission it was opened with is gone from the refreshed data — after a
 * refused change, or when a refetch on refocus no longer offers it — so Confirm cannot resend a request
 * the server will refuse.
 *
 * It waits while a request is in flight, as the dialogs themselves refuse to close then: the request's
 * own answer decides what the reader is told first. After a refused change the refusal toast already
 * says why, so the closing toast is raised only when no refusal toast is showing. An older refusal
 * left undismissed also counts; it already tells the reader to reload, which covers a later loss too.
 */
export const useCloseOnLostPermission = ({
  isOpen,
  isAvailable,
  onClose,
  message,
}: {
  isOpen: boolean
  isAvailable: boolean
  onClose: () => unknown
  /** The toast that says the dialog closed. */
  message: JSX.Element
}) => {
  const { toasts, addToast } = useToastService()
  const isRequestInFlight = useIsMutating() > 0
  const handleLoss = useEffectEvent(() => {
    onClose()
    if (![...toasts.values()].some(isRefusalToast)) {
      addToast({ message, variant: 'error' })
    }
  })
  useEffect(() => {
    if (isOpen && !isAvailable && !isRequestInFlight) {
      handleLoss()
    }
  }, [isOpen, isAvailable, isRequestInFlight])
}
